/**
 * Subscription state and the spend fuse.
 *
 * The number that matters is headroom against the monthly cap, because that is
 * what actually stops sessions starting. It is shown in dollars with a bar,
 * not as raw cents, and the cap is deliberately visible rather than something
 * a GM discovers when a session refuses to start mid-evening.
 *
 * Money arrives as JSON numbers in cents — see the Cents type in models.py.
 */
import { useState } from 'react';

import {
  openBillingPortal,
  redeemInvite,
  startCheckout,
  userFacingError,
} from '@/api/client';
import type { BillingState } from '@/api/types';

const dollars = (cents: number): string => `$${(cents / 100).toFixed(2)}`;

export function BillingCard({
  state,
  onChanged,
}: {
  state: BillingState | null;
  /** Called after a pass redeems, so the caps on screen refresh. */
  onChanged?: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorTone, setErrorTone] = useState<'gentle' | 'error'>('error');
  const [passCode, setPassCode] = useState('');
  const [passOpen, setPassOpen] = useState(false);

  function closePassForm(): void {
    setPassOpen(false);
    setPassCode('');
    setError(null);
  }

  async function redeemPass(): Promise<void> {
    const trimmed = passCode.trim();
    if (!trimmed) return;
    setBusy('pass');
    setError(null);
    setErrorTone('gentle');
    try {
      await redeemInvite(trimmed);
      setPassCode('');
      setPassOpen(false);
      onChanged?.();
    } catch (err) {
      setError(userFacingError(err, 'That pass code didn’t work. Check it and try again.'));
    } finally {
      setBusy(null);
    }
  }

  if (!state) return null;

  const active = state.subscription_status === 'active';
  const pct = Math.min(100, Math.max(0, state.percent_used));

  async function go(action: 'alpha' | 'trial' | 'portal'): Promise<void> {
    setBusy(action);
    setError(null);
    setErrorTone('error');
    try {
      const { url } =
        action === 'portal' ? await openBillingPortal() : await startCheckout(action);
      window.location.href = url;
    } catch (err) {
      setError(
        userFacingError(
          err,
          'Billing is temporarily unavailable. Please try again later.',
        ),
      );
      setBusy(null);
    }
  }

  return (
    <section className="card">
      <header className="card__head">
        <h2 className="card__title">Billing</h2>
        <span className={active ? 'badge badge--ok' : 'badge'}>
          {active ? 'Active' : state.subscription_status}
        </span>
      </header>

      {active ? (
        <>
          <div className="meter" role="img" aria-label={`${pct}% of monthly cap used`}>
            <div
              className={pct > 85 ? 'meter__fill meter__fill--warn' : 'meter__fill'}
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="card__stat">
            <strong>{dollars(state.monthly_spend_cents)}</strong> of{' '}
            {dollars(state.hard_cap_cents)} used this period ·{' '}
            {dollars(state.headroom_cents)} left
          </p>
          {state.suspended ? (
            <div className="notice notice--error" role="alert">
              Sessions are paused while Billing needs attention. Your campaigns
              and saved work are safe.
            </div>
          ) : null}
          <button
            type="button"
            className="button"
            onClick={() => void go('portal')}
            disabled={busy !== null}
          >
            {busy === 'portal' ? 'Opening…' : 'Manage subscription'}
          </button>
        </>
      ) : (
        <>
          <p className="card__body">
            A plan keeps your sessions running. $13 covers about six four-hour
            sessions a month, $4.50 about two, and you are never billed past
            the plan price.
          </p>
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="button button--primary"
              onClick={() => void go('alpha')}
              disabled={busy !== null}
            >
              {busy === 'alpha' ? 'Opening…' : 'Subscribe'}
            </button>
            <button
              type="button"
              className="button"
              onClick={() => void go('trial')}
              disabled={busy !== null}
            >
              {busy === 'trial' ? 'Opening…' : 'Try it'}
            </button>
          </div>
        </>
      )}

      {/* The trial now auto-grants on first arrival, so someone holding a
          hand-issued pass is usually already comped by the time they could
          type the code. This is where the code still lands — redeeming
          replaces the trial's small cap with the pass's larger one. */}
      {passOpen ? (
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            marginTop: 'var(--space-2)',
            flexWrap: 'wrap',
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && busy === null) closePassForm();
          }}
        >
          <input
            className="field__input"
            aria-label="Pass code"
            value={passCode}
            placeholder="TL-XXXXXX-XXXXXX-XXXXXX"
            autoComplete="off"
            spellCheck={false}
            autoFocus
            style={{ flex: '1 1 16rem' }}
            onChange={(e) => setPassCode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void redeemPass();
            }}
          />
          <button
            type="button"
            className="button"
            onClick={() => void redeemPass()}
            disabled={busy !== null || !passCode.trim()}
          >
            {busy === 'pass' ? 'Redeeming…' : 'Redeem'}
          </button>
          <button
            type="button"
            className="button button--quiet"
            onClick={closePassForm}
            disabled={busy !== null}
          >
            Cancel
          </button>
        </div>
      ) : (
        <p className="card__note" style={{ marginTop: 'var(--space-2)' }}>
          <button
            type="button"
            className="button button--quiet"
            onClick={() => {
              setError(null);
              setPassOpen(true);
            }}
          >
            Have a pass code?
          </button>
        </p>
      )}

      {error ? (
        <div
          className={
            errorTone === 'gentle' ? 'notice notice--gentle' : 'notice notice--error'
          }
          role={errorTone === 'gentle' ? 'status' : 'alert'}
        >
          {error}
        </div>
      ) : null}
    </section>
  );
}
